import { Inject } from '@odinjs/odin';
import { SuggestionCardItemAPI } from '@philips-emr/patient-components/src';
import { Button, DatePickerAPI, DeckAPI, FormAPI, LoaderAPI, ModalProps, ModalService, PanelAPI, TextAPI, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { ButtonTheme, Placement } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterNavigate, OnChange, OnClick, OnDynamicContentRender, OnMount, OnNavigate, OnSubmit, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableNavigateEvent, ChangeEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse, HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ISO_DATE_FORMAT, ISO_YEAR_MONTH_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { generateUUID, isMobile } from '@philips-internal/oswald-ui-utils';
import moment from 'moment/moment.js';
import React from 'react';
import { ApplicationModulesIds } from '../../app/module-registry/application-modules';
import { ScheduleWaitingListData } from '../../app/types';
import { AppointmentsAliases, FragmentNames } from '../../codegen';
import TokenConstants from '../../constants/TokenConstants';
import { DEFAULT_PATIENT_ID } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import Patient from '../../interfaces/patient';
import { AppointmentScheduleInfo, ScheduleItem, SuggestionGroups } from '../../interfaces/schedules-suggestions';
import AppointmentService from '../../services/appointment-service';
import AppointmentContext from '../../utils/appointment-context';
import { AppointmentUtils } from '../../utils/appointment-utils';
import { cast } from '../../utils/cast-helpers';
import { JWTTokenUtils } from '../../utils/jwt-token-utils';
import { NumberFormatUtils } from '../../utils/number-format-utils';
import { ObjectComparatorUtils } from '../../utils/object-comparator-utils';
import { defaultIfUndefined } from '../../utils/object-utils';
import { ParseHelperUtils } from '../../utils/parse-helper-utils';
import RescheduleContext from '../../utils/reschedule-utils';
import { showToastNotificationError, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import './appointments-controller.css';
import { WaitingListData } from './scheduling-waiting-list/scheduling-waiting-list-controller';
import { TeleconsultationType } from '../pseudonym-appointments/pseudonym-types';
import { PseudonymCustomMenssage } from '../pseudonym-appointments/pseudonym-interfaces';
import PrivateInsuranceConfiguration from '../../interfaces/private-insurance-configuration';

const NewScheduleTypes = {
  CONSULTATION: 'C',
  EXAM: 'E'
}

interface SpecialityData {
  cdEspecialidade: string;
  dsEspecialidade: string;
}

interface MedicalArea {
  dsAreaAtuacao: string;
  nrSequencia: number;
}

interface Physician {
  cdPessoaFisica: string;
  nmPessoaFisica: string;
}

interface ExamSchedule {
  dsImagemBase64: string;
  dsProcInterno: string;
  dsTipoExame: string;
  nrSeqProcInterno: string;
}

interface ConsultFieldItem {
  medicalArea?: MedicalArea;
  physician?: Physician;
  specialityData?: SpecialityData;
}

interface DateRange {
  end: string;
  start: string;
}

interface NewScheduleFields extends Record<string, Value>{
  consultation: ConsultFieldItem[];
  dataRage: DateRange;
  exam: ExamSchedule[];
  insurance: {
    autorizationCode: string;
    category: {
      cdCategoria: string;
      dsCategoria: string;
    };
    convenio: {
      cdConvenio: string;
      dsConvenio: string;
    };
    hasInsurance: boolean;
    healthCardNumber?: string;
    healthCardValidity?: string;
    plan: {
      cdPlano: string;
      dsPlano: string;
    };
  };
  location: {
    id: string;
    value: string;
  };
  patient: {
    nmPessoaFisica: string;
    patientId: string;
  };
  scheduleType: [{
    id: string;
    value: string;
  }];
}

interface SchedulingConfig {
  allowSelectDesiredDate: boolean;
  desiredDateCustomMessage: string;
  scheduleRangeAllowed: number;
  schedulingAllowedDays: number;
  schedulingFinalDate: Date;
}

interface ClearTimeReservation {
  nrSeqAgeintItem: number;
}

interface PhysicianAndSpeciality {
  cdEspecialidade: number;
  cdMedico: string;
}

interface WaitingListDataReduced {
  itemRemoved?: ScheduleWaitingListData;
  newData: ScheduleWaitingListData[];
}

interface CurrentPatientSelected {
  id: number;
  name: string;
}

interface ExamScheduleProps {
  exam: {
    dsImagemBase64: string;
    dsProcInterno: string;
    dsTipoExame: string;
    nrSeqProcInterno: number;
  }
}

interface SpecialityDataProps {
  cdEspecialidade?: number;
}

interface ConsultScheduleProps {
  physician?: PhysicianProps
  specialityData?: SpecialityDataProps;
}

interface PhysicianProps {
  cdPessoaFisica?: string;
  nmPessoaFisica?: string;
}

interface ScheduleTypeProps {
  id: number;
  value: string;
}

interface WaitingListDataFromCardProps {
  consultScheduleList: ConsultScheduleProps[];
  examScheduleList: ExamScheduleProps[];
  nrSeqAgeint: number;
  physician?: PhysicianProps;
  scheduleType: ScheduleTypeProps[];
}

interface AvailableCalendarDaysProps {
  availableDays: string[];
  endAt?: string;
  existsAnyBlockedItems: boolean;
  nrSeqAgeint: number;
  nrSeqAgeintItem: string;
  startAt?: string;
}

interface ParamsSeqAgenda {
  cdCategoria: string;
  cdConvenio: string;
  cdPlano: string;
  healthCardNumber: string;
  healthCardValidity: string;
  locationId: string;
  nrSeqAgeint: number,
  patientHeight?: number;
  patientId: string;
  patientWeight?: number,
}

interface SimpleObjectResponse {
  value: string;
}

interface IntegratedAgendaSequenceResponse {
  value: number;
}

interface TimeReservationResponse {
  abortMessage?: string;
}

@Controller({ component: 'layout-appointments', fragment: 'appointments' })
export default class AppointmentsController extends BaseController {

  private suggestionList: ScheduleItem[] = [];

  private haveLocation = true;
  private nrSeqAgeint!: number;
  private newScheduleFields!: NewScheduleFields;
  private selectedDateRange!: DateRange;
  private cdConvenioParticular!: string;

  private isReschedule!: boolean;

  private suggestionsAfterGetData?: ScheduleItem[];
  private blockedSuggestionsAfterGetData?: ScheduleItem[];

  @Inject('wizard-appointments')
    wizard!: WizardAPI;

  @Inject('deck-choosen-items')
    deckChoosenItems!: DeckAPI;

  @Inject('deck-blocked-items')
    deckBlockedItemsAPI!: DeckAPI;

  @Inject('text-total')
    totalText!: TextAPI;

  @Inject('panel-loader')
    panelLoader!: PanelAPI;

  @Inject('panel-deck')
    panelDeck!: PanelAPI;

  @Inject('panel-blocked-items')
    panelBlockedItemsAPI!: PanelAPI;

  @Inject('date-picker-appointment-range')
    datePickerAppointmentRange!: DatePickerAPI;

  @Inject('panel-total')
    panelTotalAPI!: PanelAPI;

  @Inject('loader-final')
    loaderFinalAPI!: LoaderAPI;

  @Inject('panel-notification-step-date')
    panelNotificationStepDateAPI!: PanelAPI;

  @Inject('panel-notification-step-suggestion')
    panelNotificationStepSuggestionAPI!: PanelAPI;

  state = {
    blockedCustomMessage: '',
    finalDateInitialRange: '',
    today: '',
  };

  @OnMount({ target: 'layout-appointments'})
  onLayoutAppointmentsMount(): void {
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: ApplicationModulesIds.APPOINTMENTS } });
    dispatchEvent(event);

    this.isReschedule = this.historyService.getCurrentRoute().includes(APPLICATION_ROUTES.RESCHEDULE_APPOINTMENTS);
    if(this.isReschedule) {
      this.getHealthInsuranceIdToContext();
    }
  }

  private getHealthInsuranceIdToContext(): void {
    HttpService.get<number>(
      APPLICATION_ENDPOINTS.HEALTH_INSURANCE_BY_NR_SEQ_AGEINT,
      {nrSeqAgeint: RescheduleContext.nrSeqAgeint, patientId: UserContext.patientId}
    ).then(healthInsuranceResponse => {
      this.context.set({healthInsuranceIdToReschedule: healthInsuranceResponse.body});
      this.setHealthInsurancePrivate().then(response => this.cdConvenioParticular = response.insuranceCode.toString());
    }).catch(() => {
      this.callToastLoadDataError();
    });
  }

  private async setHealthInsurancePrivate(): Promise<PrivateInsuranceConfiguration> {
    return HttpService.get(APPLICATION_ENDPOINTS.PARTICULAR_INSURANCE, {})
      .then(response => response.body as PrivateInsuranceConfiguration);
  }

  @OnMount(AppointmentsAliases.DECK_CHOOSEN_ITEMS)
  onMountDeckChoosenItems(event: MountEvent): void {
    const deckApi = event.target as DeckAPI;
    this.setSuggestionsOrBlockedSuggestionsIfExists(deckApi, this.suggestionsAfterGetData);
  }

  @OnMount(AppointmentsAliases.DECK_BLOCKED_ITEMS)
  onMountDeckBlockedItems(event: MountEvent): void {
    const deckApi = event.target as DeckAPI;
    this.setSuggestionsOrBlockedSuggestionsIfExists(deckApi, this.blockedSuggestionsAfterGetData);
  }

  private setSuggestionsOrBlockedSuggestionsIfExists(deckApi: DeckAPI, suggestions?: ScheduleItem[]): void {
    if (suggestions) {
      deckApi.setData(cast(suggestions));
      deckApi.setDone(true);
      suggestions = undefined;
    }
  }

  @OnUnmount({target: 'layout-appointments'})
  handleUnMountLayout(): void {
    ToastService.closeAll();
  }

  @OnChange({ target: 'date-picker-appointment-range' })
  onChangeDateRange(event: ChangeEvent<string[]>): void {
    if (event && event.value) {
      let rangeDate = event.value || [];
      if(rangeDate.length === 1) {
        rangeDate = [rangeDate[0], rangeDate[0]];
      }
      if (rangeDate.length > 1) {
        const rangeDateArray = rangeDate as unknown as [string, string];
        this.selectedDateRange = this.selectedDateRange || {}
        this.selectedDateRange.start = rangeDateArray[0] > rangeDateArray[1] ? rangeDateArray[1] : rangeDateArray[0];
        this.selectedDateRange.end = rangeDateArray[0] > rangeDateArray[1] ? rangeDateArray[0] : rangeDateArray[1];
        this.wizard.setStepDone('range-date', true);
      } else {
        this.wizard.setStepDone('range-date', false);
      }
    }
  }

  @OnMount({ target: ['date-picker-appointment-range'] })
  onMountDatePicker(): void {
    HttpService.post(APPLICATION_ENDPOINTS.GET_NEW_APPOINTMENT_SCHEDULE_CONFIG).then(response  => {
      const today = moment().utc().toISOString();
      const schedulingConfig = response.body as SchedulingConfig;
      this.datePickerAppointmentRange.setIntervalThreshold(schedulingConfig.scheduleRangeAllowed);
      const finalDate = schedulingConfig.schedulingFinalDate.toString();
      this.datePickerAppointmentRange.addRange(today, finalDate);

      this.context.set({
        allowSelectDesiredDate: schedulingConfig.allowSelectDesiredDate,
        desiredDateCustomMessage: schedulingConfig.desiredDateCustomMessage
      });

      this.state = {
        blockedCustomMessage: this.state.blockedCustomMessage,
        finalDateInitialRange: finalDate,
        today: today
      };

      if(this.isReschedule && !this.haveLocation) {
        const nrSeqAgeint = RescheduleContext.nrSeqAgeint;
        HttpService.get(
          APPLICATION_ENDPOINTS.CONSULTATION_PHYSICIAN_AND_SPECIALITY, { nrSeqAgeint, patientId: UserContext.patientId })
          .then(res => {
            const data = res.body as PhysicianAndSpeciality;

            if(data.cdMedico && data.cdEspecialidade) {
              this.setAvailableCalendarDaysForReschedule(data, nrSeqAgeint);
            }
          });
      }
    });
  }

  restoreInitialRangeDatePicker(): void {
    this.datePickerAppointmentRange.clearRanges();
    this.datePickerAppointmentRange.addRange(this.state.today, this.state.finalDateInitialRange);
  }

  selectPatientIfHaveOnlyOne(): void {
    const currentPatientId = UserContext.patientId;
    HttpService.get(APPLICATION_ENDPOINTS.NEW_APPOINTMENT_PATIENT, { patientId: currentPatientId }).then(response => {
      const currentPatientSelected = {} as CurrentPatientSelected;
      const patientList = defaultIfUndefined(response.body, []) as Patient[];

      if (!patientList.length) {
        throw Error('No patient found');
      }

      if (patientList.length === 1) {
        const [patient] = patientList;
        currentPatientSelected.id = Number(defaultIfUndefined(patient.cdPessoaFisica, DEFAULT_PATIENT_ID));
        currentPatientSelected.name = patient.nmPessoaFisica;
      } else {
        const patientSelected = patientList.find(patient => patient.cdPessoaFisica === currentPatientId.toString());
        if (!patientSelected) {
          throw Error(`No patients found with ${currentPatientId} code`);
        }

        currentPatientSelected.id = currentPatientId;
        currentPatientSelected.name = patientSelected.nmPessoaFisica;
      }

      this.context.set({ additionalInformationAlreadyExecuted: false, currentPatientSelected });
    })
  }

  /**
   * Clears the days selected in the datepicker, and returns navigation to the current date. \
   * Warning: It does not clear the value, only the tempValue.
   */
  clearTempValueDatePicker(): void {
    this.datePickerAppointmentRange.setInitialDate(moment().utc().toISOString());
    this.datePickerAppointmentRange.setValue([]);
  }

  @OnAfterNavigate({ target: 'wizard-appointments' })
  async onAfterNavigateWizard(event: AbortableNavigateEvent<string>): Promise<void> {
    this.onWizardNavigate(event);
    const fieldSchedulingListSize = 1;

    if(event.to === 'range-date') {
      // This forces the user to always click on DatePicker's select button.
      this.wizard.setStepDone('range-date', false);

      this.panelNotificationStepDateAPI.setVisible(false);
      this.setWaitingListDataToContext();
    }

    if(event.to === 'range-date' && event.from !== 'suggestion') {
      this.clearTempValueDatePicker();
    }

    if(event.from === 'suggestion') {
      if (this.panelDeck.isVisible()) {
        this.deckChoosenItems.setData([]);
      }
      if (this.panelBlockedItemsAPI.isVisible()) {
        this.deckBlockedItemsAPI.setData([]);
      }
    }

    if(event.to === 'range-date' && fieldSchedulingListSize > 1) {
      this.restoreInitialRangeDatePicker();
    } else if(event.to === 'range-date') {

      const cdEspecialidade = String(this.context.get('cdEspecialidade')).trim();
      const cdMedico = String(this.context.get('cdMedico')).trim();

      if(cdEspecialidade.length === 0 || cdMedico.length === 0) {
        this.restoreInitialRangeDatePicker();
        return;
      }

      const response = await this.getAvailableCalendarDays();
      this.handleAvailableCalendarDaysResponse(response);
    }
  }

  private handleAvailableCalendarDaysResponse(response: AvailableCalendarDaysProps): void {
    if(response) {
      const { availableDays, existsAnyBlockedItems } = response;
      if(availableDays) {
        availableDays.forEach(date =>  this.datePickerAppointmentRange.addRange(date, date));
      }
      const ieListaEspConsultaEnabled = 'S' === JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_CONSULTA);
      const isNotificationVisible = ieListaEspConsultaEnabled && availableDays == null && !existsAnyBlockedItems;
      this.panelNotificationStepDateAPI.setVisible(isNotificationVisible);
    } else {
      this.restoreInitialRangeDatePicker();
    }
  }

  @OnNavigate({target: 'date-picker-appointment-range'})
  async onNavigateDatePicker(event : AbortableNavigateEvent<string>): Promise<void> {
    this.panelNotificationStepDateAPI.setVisible(false);
    const fieldSchedulingListSize = Number(this.context.get('fieldSchedulingListSize'));
    const isOnlyConsultation = this.context.get('isOnlyConsultation');
    const cdEspecialidade = String(this.context.get('cdEspecialidade')).trim();
    const cdMedico = String(this.context.get('cdMedico')).trim();

    if(!isOnlyConsultation
      || !cdEspecialidade
      || !cdMedico
      || fieldSchedulingListSize > 1) {
      return;
    }

    const currentDate = moment.utc();
    const goToDate = moment.utc(event.to && event.to[0]);
    const lastCalendarDate = goToDate.add(1, 'months');

    if(lastCalendarDate > currentDate) {
      const ranges = this.datePickerAppointmentRange.getRanges();
      for(const range of ranges) {
        if(range.startDate.includes(lastCalendarDate.format(ISO_YEAR_MONTH_FORMAT))) {
          return;
        }
      }

      const response = await this.getAvailableCalendarDays(lastCalendarDate.format(ISO_DATE_FORMAT));
      this.handleAvailableCalendarDaysResponse(response);
    }
  }

  /**
   * - For empty lastCalendarDate, the available calendar days follow
   * the backend criteria.
   * - For not empty lastCalendarDate the available calendar
   * days are limited to date informed in the string.
   * @param lastCalendarDate of type string.
   * @returns string[] with available dates.
   */
  async getAvailableCalendarDays(lastCalendarDate = '' as string): Promise<AvailableCalendarDaysProps> {
    this.newScheduleFields = {} as unknown as NewScheduleFields;

    const patientId = this.newScheduleFields.patient.patientId ? this.newScheduleFields.patient.patientId : UserContext.patientId;

    const paramsSeqAgenda: ParamsSeqAgenda = {
      cdCategoria: this.newScheduleFields.insurance?.category?.cdCategoria ?? '',
      cdConvenio: (String(this.newScheduleFields.insurance.convenio.cdConvenio)) || '',
      cdPlano: this.newScheduleFields.insurance?.plan?.cdPlano ?? '',
      healthCardNumber: this.newScheduleFields.insurance.healthCardNumber || '',
      healthCardValidity: this.newScheduleFields.insurance.healthCardValidity || '',
      locationId: '',
      nrSeqAgeint: 0,
      patientId: this.newScheduleFields.patient.patientId || '0',
    };

    try {
      const waitingListData = this.context.get('waitingListData') as Record<string, Value>;
      this.datePickerAppointmentRange.clearRanges();
      this.datePickerAppointmentRange.setLoading(true);

      if(this.isReschedule) {
        const response = await HttpService.get(APPLICATION_ENDPOINTS.AVAILABLE_CALENDAR_DAY, {
          lastCalendarDate,
          locationId: 0,
          nrSeqAgeint: RescheduleContext.nrSeqAgeint,
          patientId: patientId
        });
        this.datePickerAppointmentRange.setLoading(false);
        const result = response.body as AvailableCalendarDaysProps;
        this.context.set({waitingListData: {...waitingListData, nrSeqAgeint: RescheduleContext.nrSeqAgeint, nrSeqAgeintItem: result.nrSeqAgeintItem}});
        return result;
      }

      let response = await HttpService.get(APPLICATION_ENDPOINTS.GET_PREVIOUS_SEQ_AGENDA_INTEGRADA, {...paramsSeqAgenda});
      let nrSeqAgeint = response.body as number;
      const hasSeqAgendaIntegrada = nrSeqAgeint > 0;
      paramsSeqAgenda.nrSeqAgeint = nrSeqAgeint;

      const nrSeqAgeintObtained = await HttpService.get<IntegratedAgendaSequenceResponse>(
        APPLICATION_ENDPOINTS.GET_SEQ_AGENDA_INTEGRADA,
        {...paramsSeqAgenda}
      ).then(({ body }) => body?.value);

      nrSeqAgeint = nrSeqAgeint > 0 ? nrSeqAgeint : defaultIfUndefined(nrSeqAgeintObtained, 0);

      const paramsList = this.getParamsListAppointmentScheduleInfo(nrSeqAgeint, hasSeqAgendaIntegrada);

      response = await HttpService.post(APPLICATION_ENDPOINTS.GET_SEQ_PROC_INTERNO, { nrSeqAgeint, patientId }, {body: paramsList as unknown as Record<string, Value>});
      if(response.status === 204) {
        response = await HttpService.get(APPLICATION_ENDPOINTS.AVAILABLE_CALENDAR_DAY, {
          lastCalendarDate,
          locationId: 0,
          nrSeqAgeint: nrSeqAgeint,
          patientId: patientId
        });
        this.datePickerAppointmentRange.setLoading(false);
        const result = response.body as AvailableCalendarDaysProps;

        this.context.set({waitingListData: {...waitingListData, nrSeqAgeint: nrSeqAgeint, nrSeqAgeintItem: result.nrSeqAgeintItem}});
        return result;
      }

      return {
        availableDays: [],
        existsAnyBlockedItems: false,
        nrSeqAgeint,
        nrSeqAgeintItem: '',
      } as AvailableCalendarDaysProps;
    } catch(error) {
      this.restoreInitialRangeDatePicker();
      this.datePickerAppointmentRange.setLoading(false);
      return {
        availableDays: [],
        nrSeqAgeintItem: '',
      } as unknown as AvailableCalendarDaysProps;
    }
  }

  setAvailableCalendarDaysForReschedule(data: PhysicianAndSpeciality, nrSeqAgeint: number): void {
    const { cdEspecialidade, cdMedico } = data;

    this.context.set({
      cdEspecialidade,
      cdMedico,
      fieldSchedulingListSize: 1,
      isOnlyConsultation: true,
    });
    this.clearTempValueDatePicker();

    try {
      this.datePickerAppointmentRange.clearRanges();
      this.datePickerAppointmentRange.setLoading(true);

      HttpService.get(APPLICATION_ENDPOINTS.AVAILABLE_CALENDAR_DAY, {
        lastCalendarDate: '',
        nrSeqAgeint,
        patientId: UserContext.patientId
      })
        .then(calendarResponse => {
          this.datePickerAppointmentRange.setLoading(false);
          const availableDays = calendarResponse.body as [];
          availableDays.forEach(date =>  this.datePickerAppointmentRange.addRange(date, date));
        });
    } catch(error) {
      this.restoreInitialRangeDatePicker();
      this.datePickerAppointmentRange.setLoading(false);
    }
  }

  onWizardNavigate(event: AbortableNavigateEvent<string>): void {
    switch(event.to) {
      case 'suggestion':
        this.sendInfo();
        break;
    }
  }

  private checkIfNewScheduleFieldsWasChanged = (oldScheduleFields: Record<string, Value>, newScheduleFields: Record<string, Value>, fieldsWasChanged: boolean): boolean => {
    const oldValuesKeys = Object.keys(oldScheduleFields);
    const newValuesKeys = Object.keys(newScheduleFields);

    if (oldValuesKeys.length !== newValuesKeys.length) {
      fieldsWasChanged = false;
      return fieldsWasChanged;
    }

    oldValuesKeys.forEach(value => {
      const oldValue = oldScheduleFields[value] as Record<string, Value>;
      const newValue = newScheduleFields[value] as Record<string, Value>;
      const areObjects = ObjectComparatorUtils.isObject(oldValue) && ObjectComparatorUtils.isObject(newValue);

      if (areObjects && !this.checkIfNewScheduleFieldsWasChanged(oldValue, newValue, fieldsWasChanged) || !areObjects && oldValue !== newValue) {
        fieldsWasChanged = false;
        return;
      }

    });

    return fieldsWasChanged;
  }

  private getParamsListAppointmentScheduleInfo = (nrSeqAgeint: number, hasSeqAgendaIntegrada: boolean): AppointmentScheduleInfo[] => {
    const paramsList: AppointmentScheduleInfo[] = [];
    if (this.newScheduleFields.consultation && this.newScheduleFields.consultation.length > 0) {
      this.newScheduleFields.consultation.forEach(consult => {
        paramsList.push({
          cdEspecialidade: (consult?.specialityData?.cdEspecialidade) || '',
          cdMedico: (consult.physician && consult.physician.cdPessoaFisica) || '',
          hasSeqAgendaIntegrada: hasSeqAgendaIntegrada,
          ieTipoItem: NewScheduleTypes.CONSULTATION,
          nrSeqAgeint: nrSeqAgeint,
          nrSeqMedicalArea: consult.medicalArea?.nrSequencia,
        });
      });
    }

    if (this.newScheduleFields.exam && this.newScheduleFields.exam.length > 0) {
      this.newScheduleFields.exam.forEach(exam => {
        paramsList.push({
          hasSeqAgendaIntegrada: hasSeqAgendaIntegrada,
          ieTipoItem: NewScheduleTypes.EXAM,
          nrSeqAgeint: nrSeqAgeint,
          nrSeqProcInterno: exam.nrSeqProcInterno,
        });
      });
    }

    return paramsList;
  }

  @OnDynamicContentRender({ target: 'deck-choosen-items' })
  onDeckGroupRender(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const scheduleItem = event.data as ScheduleItem[];
      const hasSchedulingTimes = scheduleItem.some(item => item.hasSchedulingTimes);
      const renderSuggestionDate: () => JSX.Element = () => {
        return (
          <>
            {hasSchedulingTimes && <div className="wrapper-full-click" onClick={() => this.openModalSuggestion(scheduleItem[0].groupId)}></div>}
            <div className='deck-title-suggestion-group'>
              {hasSchedulingTimes
                ?
                <Button
                  value={MessageService.translate('patient.choose-combo')}
                  theme={ButtonTheme.QUIET_EMPHASIS}
                  icon='/static/assets/icons/DLS_Calendar_32.svg'
                  iconPlacement={Placement.RIGHT}
                  onClick={() => this.openModalSuggestion(scheduleItem[0].groupId)}
                />
                : <p className='p-deck-title-label__not-has-scheduling'>{MessageService.translate('suggestion.lbl-not-has-scheduling')}</p>
              }
            </div>
          </>
        );
      }
      render(renderSuggestionDate);
    }
  }

  @OnDynamicContentRender({ target: 'deck-blocked-items' })
  handleDeckBlockedItemsRender(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const renderBlockedSuggestions: () => JSX.Element = () => {
        return (
          <div className='deck-title-suggestion-group'>
            <p className='p-deck-title-label__not-has-scheduling'>{ this.state.blockedCustomMessage ? this.state.blockedCustomMessage : MessageService.translate('tws.label-blocked-by-health-insurance')}</p>
          </div>
        );
      }
      render(renderBlockedSuggestions);
    }
  }

  @OnSubmit({ target: 'wizard-appointments' })
  onFinishWizard(event: SubmitEvent): void {
    const waitingListData = this.context.get('waitingListArrayToPersistence') as ScheduleWaitingListData[] || [];
    if (event) {
      setTimeout(() => {
        this.panelLoader.setVisible(true);
        this.loaderFinalAPI.setMessage(MessageService.translate('tws.loader.loading'));
      });
      this.wizard.setStepDone('suggestion', false);
      let confirmAppointmentInfoList: Record<string, Value>[] = [];
      if(this.suggestionList && this.suggestionList.length > 0) {
        this.suggestionList.forEach(suggestion => {
          const waitingListItem = waitingListData.find(item => item.nrSeqAgeintItem === suggestion.nrSeqAgeintItem);
          if(!waitingListItem && suggestion.hasSchedulingTimes) {
            confirmAppointmentInfoList.push({
              attachedFileList: suggestion.attachedPrescheduleFiles,
              availableType: suggestion.availableType,
              cdAgenda: suggestion.agendaId,
              cdEspecialidade: suggestion.cdEspecialidade,
              cdEstabelecimento: suggestion.cdEstabelecimento,
              cdMedico: suggestion.cdMedico && suggestion.cdMedico.length > 0 ? suggestion.cdMedico : null,
              cdPessoaFisica: this.newScheduleFields.patient.patientId,
              dsNota: suggestion.observation,
              dtAgenda: suggestion.selectedDate,
              ieTipoAgendamento: suggestion.type,
              nmPreferredTime: suggestion.period,
              nrSeqAgeintItem: suggestion.nrSeqAgeintItem,
              nrSeqAgenda: suggestion.nrSeqAgenda,
              nrSeqProcInterno: suggestion.nrSeqProcInterno,
              suggestionTypeConsultation: suggestion.suggestionTypeConsultation
            });
          }
        });
      }
      if(waitingListData.length > 0) {
        confirmAppointmentInfoList = [...confirmAppointmentInfoList, ...waitingListData as unknown as Record<string, Value>[]];
      }

      const patientCode = this.newScheduleFields.patient.patientId ? Number.parseInt(this.newScheduleFields.patient.patientId) : UserContext.patientId;
      AppointmentService.confirmAndRequestAppointments(this.nrSeqAgeint, patientCode, confirmAppointmentInfoList).then(body => {
        this.wizard.setStepDone('suggestion', false);
        ModalService.openFullscreen({
          className: 'schedule-confirmation-modal-custom-bg',
          closeable: true,
          contextData: { data: body },
          dockButtons: [],
          fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_CONFIRMATION,
          onClose: () => {
            this.panelLoader.setVisible(false);
            this.historyService.push(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENTS, {}, {scrollToTop: true});
          },
          title: MessageService.translate('patient.schedule-success'),
        })
      }).catch(() => {
        this.wizard.setStepDone('suggestion', true);
        showToastNotificationError(MessageService.translate('patient.appointments.confirm-failed'));
      }).finally(() => {
        this.panelLoader.setVisible(false);
      })
    }
  }

  getSuggestions(nrSeqAgeint: number, patientId: number): void {
    HttpService.get(APPLICATION_ENDPOINTS.SUGGESTION_DATES, {
      finalDate: this.newScheduleFields.dataRage.end,
      initialDate: this.newScheduleFields.dataRage.start,
      locationId: 0,
      nrSeqAgeint: nrSeqAgeint,
      patientId: patientId,
    }).then(response => {
      const ieListaEspConsulta = JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_CONSULTA);
      const ieListaEspExame = JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_EXAME);
      const isEnabledToScheduleWaitingList = ieListaEspConsulta === 'S' && ieListaEspExame === 'S';
      const suggestionGroups = response.body as SuggestionGroups[];
      const isShowPrice = this.isShowSchedulePrice();

      let groupId = 0
      let isShowNotificationWaitingList = false;
      this.suggestionList = [] as ScheduleItem[];
      const blockedSuggestions = [] as ScheduleItem[];
      suggestionGroups.forEach(group => {
        if(!isShowNotificationWaitingList) {
          isShowNotificationWaitingList = (
            (!group.establishmenSuggestionsGroups || group.establishmenSuggestionsGroups.length === 0) &&
            (!group.blockedItems || group.blockedItems.length === 0)
          )
        }
        group.groupId = groupId;
        group.nrSeqAgeint = nrSeqAgeint;
        group.isShowPrice = isShowPrice;
        group.scheduleItems && group.scheduleItems.forEach(suggestion => {
          this.suggestionList.push({
            agendaId: '',
            availableType: suggestion.availableType,
            cdEspecialidade: suggestion.cdEspecialidade || '',
            cdEstabelecimento: suggestion.cdEstabelecimento,
            cdMedico: suggestion.cdMedico || '',
            copagoPrice: '0',
            copagoPriceNoFormatted: 0,
            date: suggestion.date,
            dsEstablishment: '',
            dsProcInterno: suggestion.dsProcInterno,
            endDate: '',
            groupId: group.groupId,
            hasSchedulingTimes: group.establishmenSuggestionsGroups.length > 0,
            ieAcao: '',
            ieReturn: suggestion.ieReturn,
            image: suggestion.image,
            isEnabledToScheduleWaitingList,
            isItemFromReschedule: this.isReschedule,
            isShowPrice: suggestion.isShowPrice,
            key: suggestion.key,
            nrMinutoDuracao: 0,
            nrSeqAgeint: suggestion.nrSeqAgeint,
            nrSeqAgeintItem: suggestion.nrSeqAgeintItem,
            nrSeqAgenda: '',
            nrSeqProcInterno: suggestion.nrSeqProcInterno,
            observation:suggestion.observation,
            period: suggestion.period,
            price: '0',
            priceNoFormatted: 0,
            qtPesoMaxAgenda: '',
            quantityClassification : suggestion.quantityClassification,
            quantityClassificationTelehealth : suggestion.quantityClassificationTelehealth,
            ruleClassification : suggestion.ruleClassification,
            ruleClassificationTelehealth : suggestion.ruleClassificationTelehealth,
            selectedDate: '',
            shouldDisplayTheConsultationValue:'N',
            shouldDisplayTheExamValue: 'N',
            startDate: '',
            subtitle1: suggestion.subtitle1,
            suggestionGroupId: groupId,
            suggestionId: suggestion.suggestionId,
            suggestionTypeConsultation: suggestion.suggestionTypeConsultation,
            title: suggestion.title,
            type: suggestion.type,
          });
        });
        groupId++;
        group.blockedItems && group.blockedItems.forEach(blockedItem => {
          blockedSuggestions.push({
            dsEstablishment: '',
            groupId: -1,
            isShowPrice: false,
            selectedDate: '',
            title: blockedItem.dsItemGridAgeint,
          } as ScheduleItem);
        });
      })
      setTimeout(() => {
        const doesNotExistsSuggestions = this.suggestionList.every(suggestion => !suggestion.hasSchedulingTimes);
        this.context.set({ allSuggestionsHaveBeenSelected: false, doesNotExistsSuggestions  });
        this.panelLoader.setVisible(false);
        this.panelNotificationStepSuggestionAPI.setVisible(isShowNotificationWaitingList && isEnabledToScheduleWaitingList && !this.isReschedule);
        if(this.suggestionList.length > 0) {
          this.panelDeck.setVisible(true);
          this.suggestionsAfterGetData = this.suggestionList;
          this.context.set({ suggestionGroups: suggestionGroups });
        }
        if(blockedSuggestions.length > 0) {
          HttpService.get(APPLICATION_ENDPOINTS.GET_WSUITE_CUSTOM_MESSAGE, { action: 'BA' }).then(resp => {
            const customMessage = resp.body as PseudonymCustomMenssage;
            this.state.blockedCustomMessage = !customMessage.message ? '' : customMessage.message
          }).finally(() => {
            this.panelBlockedItemsAPI.setVisible(true);
            this.blockedSuggestionsAfterGetData = blockedSuggestions;
          });
        }
      })
    }).catch(this.callToastLoadDataError);
  }

  sendInfo(): void {
    this.context.set({ waitingListArrayToPersistence: [] });
    this.panelNotificationStepSuggestionAPI.setVisible(false);

    this.totalText.setValue('');
    this.panelLoader.setVisible(true);

    if (this.panelDeck.isVisible()) {
      this.deckChoosenItems.setData([]);
      this.deckChoosenItems.setDone(true);
    }

    if (this.panelBlockedItemsAPI.isVisible()) {
      this.deckBlockedItemsAPI.setData([]);
      this.deckBlockedItemsAPI.setDone(true);
    }

    this.panelDeck.setVisible(false);
    this.panelBlockedItemsAPI.setVisible(false);
    this.context.set({ suggestionGroups: [] });

    if(this.isReschedule) {
      this.sendRescheduleInfo();
    } else {
      this.sendScheduleInfo();
    }
  }

  sendScheduleInfo(): void {
    this.newScheduleFields = {} as unknown as NewScheduleFields;
    this.setDateRange();

    const paramsSeqAgenda: ParamsSeqAgenda = {
      cdCategoria: (this.newScheduleFields.insurance && this.newScheduleFields.insurance.category && this.newScheduleFields.insurance.category.cdCategoria) || '',
      cdConvenio: (String(this.newScheduleFields.insurance.convenio.cdConvenio)) || '',
      cdPlano: (this.newScheduleFields.insurance && this.newScheduleFields.insurance.plan && this.newScheduleFields.insurance.plan.cdPlano) || '',
      healthCardNumber: this.newScheduleFields.insurance.healthCardNumber || '',
      healthCardValidity: this.newScheduleFields.insurance.healthCardValidity || '',
      locationId: '',
      nrSeqAgeint: 0,
      patientId: this.newScheduleFields.patient.patientId || '0',
    };

    this.nrSeqAgeint = 0;

    const { panelLoader } = this;
    HttpService.get(APPLICATION_ENDPOINTS.GET_PREVIOUS_SEQ_AGENDA_INTEGRADA, {...paramsSeqAgenda}).then(responsePrevSeq => {
      let nrSeqAgeint = responsePrevSeq.body as number;
      const hasSeqAgendaIntegrada = nrSeqAgeint > 0;
      paramsSeqAgenda.nrSeqAgeint = nrSeqAgeint;

      HttpService.get<IntegratedAgendaSequenceResponse>(APPLICATION_ENDPOINTS.GET_SEQ_AGENDA_INTEGRADA, {...paramsSeqAgenda}).then(async responseSegSchedule => {
        nrSeqAgeint = nrSeqAgeint > 0 ? nrSeqAgeint : responseSegSchedule.body?.value as number;
        this.nrSeqAgeint = nrSeqAgeint;

        const paramsList: AppointmentScheduleInfo[] = this.getParamsListAppointmentScheduleInfo(this.nrSeqAgeint, hasSeqAgendaIntegrada);

        HttpService.post(APPLICATION_ENDPOINTS.GET_SEQ_PROC_INTERNO, { nrSeqAgeint, patientId: paramsSeqAgenda.patientId }, {
          body: paramsList as unknown as Record<string, Value> })
          .then(() => {
            this.getSuggestions(nrSeqAgeint, Number(paramsSeqAgenda.patientId || 0));
          }).catch(() => {
            this.callToastLoadDataError();
            panelLoader.setVisible(false);
          });
      }).catch(() => {
        this.callToastLoadDataError();
        panelLoader.setVisible(false);
      });
    }).catch(() => {
      this.callToastLoadDataError();
      panelLoader.setVisible(false);
    })
  }

  private callToastLoadDataError(): void {
    showToastNotificationError(MessageService.translate('tws.toast-notification.load-data-error'));
  }

  sendRescheduleInfo(): void {
    const { patientId } = UserContext
    this.newScheduleFields = {} as unknown as NewScheduleFields;
    this.newScheduleFields.patient = {
      nmPessoaFisica: '',
      patientId: patientId?.toString(),
    };

    this.setDateRange();
    this.nrSeqAgeint = RescheduleContext.nrSeqAgeint;
    this.getSuggestions(RescheduleContext.nrSeqAgeint, patientId);
  }

  setDateRange(): void {
    this.newScheduleFields.dataRage = this.selectedDateRange;
  }

  openModalSuggestion(groupId: number): void {
    AppointmentContext.callUpdateAppointmentCard = this.setSelectedSuggestion.bind(this);
    const suggestionGroups = this.context.get('suggestionGroups') as SuggestionGroups[];
    ModalService.openFullscreen({
      closeable: true,
      contextData: {
        allowSelectDesiredDate: this.context.get('allowSelectDesiredDate'),
        data: suggestionGroups[groupId] as unknown as Record<string, string>,
        desiredDateCustomMessage: this.context.get('desiredDateCustomMessage'),
        nrSeqAgeint: RescheduleContext.nrSeqAgeint
      },
      dockButtons: [],
      fragment: 'sugestion-list', //fix name
      onSubmit: () => undefined,
      title: '',
    });
  }

  private checkReservationTime(newSelectedList: ScheduleItem[]): ClearTimeReservation[] {
    const clearTimeReservationList: ClearTimeReservation[] = [];
    for(const newItem of newSelectedList) {
      const itemToRemove = this.suggestionList.filter(currentItemSet =>
        Number(currentItemSet.nrSeqAgeintItem) === Number(newItem.nrSeqAgeintItem) &&
        currentItemSet.availableType !== 'R' && newItem.availableType === 'R'
      );

      if(itemToRemove.length === 1) {
        clearTimeReservationList.push({nrSeqAgeintItem: Number(itemToRemove[0].nrSeqAgeintItem)});
      }
    }

    return clearTimeReservationList;
  }

  private async registerTimeSchedule(groupId: number, selectedList: ScheduleItem[]): Promise<boolean> {
    try {
      const clearTimeReservationList = this.checkReservationTime(selectedList);
      if(clearTimeReservationList.length > 0) {
        await this.clearTimeReservation(clearTimeReservationList);
      }

      let isReservationRegistered = true;
      const suggestionGroups = this.context.get('suggestionGroups') as SuggestionGroups[];
      for(const suggestion of selectedList) {
        if(suggestion.availableType !== 'R') {
          const bodyData = {
            cdAgenda: suggestion.agendaId,
            cdMedico: suggestion.cdMedico,
            hrAgenda: suggestion.startDate,
            ieAcao: 'I',
            nrMinutoDuracao: suggestion.nrMinutoDuracao,
            nrSeqAgeint: suggestionGroups[groupId].nrSeqAgeint,
            nrSeqAgeintItem: suggestion.nrSeqAgeintItem,
            nrSeqAgenda: suggestion.nrSeqAgenda,
            nrSeqCombo: suggestion.suggestionId,
            qtPesoMaxAgenda: suggestion.qtPesoMaxAgenda === '0' ? '' : suggestion.qtPesoMaxAgenda,
          }
          if (suggestion.suggestionTypeConsultation == TeleconsultationType.TELECONSULTATION && suggestion.quantityClassificationTelehealth > 0) {
            bodyData.nrMinutoDuracao = suggestion.quantityClassificationTelehealth;
          } else if (suggestion.suggestionTypeConsultation == TeleconsultationType.IN_PERSON && suggestion.quantityClassification > 0) {
            bodyData.nrMinutoDuracao = suggestion.quantityClassification;
          }
          const response = await HttpService.post<TimeReservationResponse>(APPLICATION_ENDPOINTS.TIME_RESERVATION, { patientId: UserContext.patientId }, { body: bodyData });
          const message = response.body?.abortMessage;
          if(message != null && message.length > 0) {
            this.openModalSuggestion(groupId);
            showToastNotificationWarning(message);
            isReservationRegistered = false;
          }
        }
      }
      return isReservationRegistered;
    } catch(error) {
      this.callToastLoadDataError();
      return false;
    }
  }

  private async clearTimeReservation(clearTimeReservationList: ClearTimeReservation[]): Promise<HttpResponse<boolean>> {
    return HttpService.delete(APPLICATION_ENDPOINTS.CLEAR_TIME_RESERVATION, { patientId: UserContext.patientId },
      {body: clearTimeReservationList as unknown as Record<string, Value>}
    );
  }

  setSelectedSuggestion(groupId: number, selectedList: ScheduleItem[] = []): void {
    if (selectedList.length == 0) {
      return;
    }

    this.registerTimeSchedule(groupId, selectedList).then(isReservationRegistered => {
      if(!isReservationRegistered) {
        return;
      }

      let total = 0;
      let currencySymbol = '';
      let allSelected = true;
      const selectedDates = selectedList;
      const waitingListData = this.context.get('waitingListArrayToPersistence') as ScheduleWaitingListData[] || [];
      this.suggestionList.forEach(item => {
        const selectedDate = selectedDates.find(suggestion => {
          return item.nrSeqAgeintItem.toString() === suggestion.nrSeqAgeintItem.toString();
        });
        if (item.groupId === groupId) {
          if (selectedDate) {
            item.agendaId = selectedDate.agendaId;
            item.availableType = selectedDate.availableType;
            item.cdEspecialidade = selectedDate.cdEspecialidade;
            item.cdEstabelecimento = selectedDate.cdEstabelecimento;
            item.cdMedico = selectedDate.cdMedico;
            item.dsEstablishment = selectedDate.dsEstablishment;
            item.ieAcao = selectedDate.ieAcao;
            item.ieReturn = selectedDate.ieReturn;
            item.image = selectedDate.image;
            item.nrSeqAgenda = String(selectedDate.nrSeqAgenda);
            item.nrSeqProcInterno = selectedDate.nrSeqProcInterno;
            item.observation = selectedDate.observation;
            item.period = selectedDate.period;
            item.price = selectedDate.price;
            item.priceNoFormatted = selectedDate.priceNoFormatted;
            item.qtPesoMaxAgenda = String(selectedDate.qtPesoMaxAgenda);
            item.selectedDate = selectedDate.date;
            item.startDate = selectedDate.startDate;
            item.subtitle1 = selectedDate.subtitle1;
            item.title = selectedDate.title;
            item.isShowPrice = selectedDate.isShowPrice;
            item.attachedPrescheduleFiles = selectedDate.attachedPrescheduleFiles;
            item.appointmentType = selectedDate.appointmentType;
            item.shouldDisplayTheConsultationValue = selectedDate.shouldDisplayTheConsultationValue;
            item.shouldDisplayTheExamValue= selectedDate.shouldDisplayTheExamValue;
            item.nrSeqAgeint = selectedDate.nrSeqAgeint,
            item.teleconsultationType = selectedDate.teleconsultationType
            item.suggestionTypeConsultation = selectedDate.suggestionTypeConsultation
          }
        } else if(!selectedDate){
          const isOnTheWaitingList = waitingListData.some(row => Number(row.nrSeqAgeintItem) === Number(item.nrSeqAgeintItem));
          item.isWaitingListItem = isOnTheWaitingList;
          item.removeItemFromWaitingList = this.removeWaitingListItemToContext.bind(this);
          item.callToWaitlistModal = this.showWaitingListModal.bind(this);
        }
        if (!item.ieReturn) {
          const matched = item.price.trim().match(/(\S+) /);
          if(matched != null && !currencySymbol) {
            currencySymbol = matched[0];
          }
          total = item.priceNoFormatted + total;
        }
        if (!item.selectedDate && item.hasSchedulingTimes) {
          allSelected = false;
        }
      })
      this.setTotalTotalScheduleAndShow(total, this.suggestionList, currencySymbol);
      this.deckChoosenItems.setData(this.suggestionList as unknown as Record<string, Value>[]);
      this.deckChoosenItems.setDone(true);
      this.context.set({ allSuggestionsHaveBeenSelected: allSelected });
      if (allSelected) {
        this.wizard.setStepDone('suggestion', true);
      } else {
        this.wizard.setStepDone('suggestion', false);
      }
    });
  }

  setTotalTotalScheduleAndShow(total: number, suggestionItems: ScheduleItem[],currencySymbol?: string): void {
    const isShowPrice = this.isShowSchedulePrice() && AppointmentUtils.isShowTotal(suggestionItems);
    this.panelTotalAPI.setVisible(isShowPrice);

    if(isShowPrice) {
      const totalFormatted = NumberFormatUtils.formatToCurrentLanguage(total, currencySymbol);
      const totalLabel = ' ' + ParseHelperUtils.parseToTextHtml(totalFormatted);
      this.totalText.setValue(MessageService.translate('tws.label-total') + totalLabel);
    }
  }

  isShowSchedulePrice(): boolean {
    if(this.isReschedule) {
      const contextValue = this.context.get('healthInsuranceIdToReschedule') || this.cdConvenioParticular;
      const healthInsuranceIdReschedule = contextValue != null ? contextValue : this.cdConvenioParticular;
      this.newScheduleFields.insurance = {
        autorizationCode: '',
        category: {
          cdCategoria: '',
          dsCategoria: '',
        },
        convenio: {
          cdConvenio: healthInsuranceIdReschedule.toString(),
          dsConvenio: '',
        },
        hasInsurance: Number(healthInsuranceIdReschedule) !== Number(this.cdConvenioParticular),
        plan: {
          cdPlano: '',
          dsPlano: ''
        },
      }
    }
    const healthInsuranceId = Number(this.newScheduleFields.insurance.convenio.cdConvenio);
    const privateHealthInsuranceId = Number(this.cdConvenioParticular);
    return healthInsuranceId === privateHealthInsuranceId;
  }

  getTasyParams(): void {
    this.getShowMedicalAreaValueFromTasy();
  }

  getShowMedicalAreaValueFromTasy(): Promise<void> {
    const currentFieldMedicalAreaIsVisible = this.context.get('fieldMedicalAreaIsVisible');
    if(currentFieldMedicalAreaIsVisible != null) {
      return Promise.resolve();
    }
    return HttpService.get<SimpleObjectResponse>(APPLICATION_ENDPOINTS.TASY_MEDICAL_AREA_PARAMETER).then((item) => {
      const tasyMedicalAreaParameter = item.body?.value;
      if (!tasyMedicalAreaParameter || tasyMedicalAreaParameter === 'N') {
        this.context.set({ fieldMedicalAreaIsVisible: false });
      } else {
        this.context.set({ fieldMedicalAreaIsVisible: true });
      }
    })
  }

  @OnMount({target: AppointmentsAliases.SUGGESTION_CARD_ITEM_CHOOSEN_ITEM_APPOINTMENTS})
  handleSuggestionCardLoading(event: MountEvent): void {
    const suggestionCard = event.target as SuggestionCardItemAPI;
    suggestionCard.setFunctionToCallWaitingListModal(this.showWaitingListModal.bind(this));
    suggestionCard.setFunctionToRemoveItemFromWaitingListModal(this.removeWaitingListItemToContext.bind(this));
  }

  private showWaitingListModal = (waitingListDataFromCard?: WaitingListDataFromCardProps): void => {
    const selectedPeriod = this.newScheduleFields?.dataRage;
    let availablePeriod: string[] = [];
    if(selectedPeriod?.start) {
      availablePeriod = [moment.utc(selectedPeriod.start).format('YYYY-MM-DD'), moment.utc(selectedPeriod.end).format('YYYY-MM-DD')];
    }

    const waitingListDataTemp = this.context.get('waitingListData') as WaitingListData;
    const waitingListData = waitingListDataFromCard
      ? {...waitingListDataTemp, ...waitingListDataFromCard, availablePeriod }
      : waitingListDataTemp;

    const haveToReturnWaitingListItem = !!waitingListDataFromCard;
    const modalProps: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { haveToReturnWaitingListItem, waitingListData },
      focusOnOpen: true,
      fragment: 'scheduling-waiting-list',
      noPadding: true,
      onSubmit: event => this.handleSubmitWaitingListModalByCard(event),
      title: MessageService.translate('patient.label.modal-scheduling-waiting-list'),
      uuid: generateUUID(),
    }

    isMobile() ? ModalService.openFullscreen(modalProps): ModalService.openDialog(modalProps);
  }

  private removeWaitingListItemToContext(nrSeqAgeintItem: string): void {
    const waitingListArrayToPersistenceFromContext = this.context.get('waitingListArrayToPersistence') as ScheduleWaitingListData[] || [];
    const waitingListReduced = waitingListArrayToPersistenceFromContext.reduce<WaitingListDataReduced>((waitingList, item) => {
      if(item.nrSeqAgeintItem === nrSeqAgeintItem) {
        waitingList.itemRemoved = item;
      } else {
        waitingList.newData = [...waitingList.newData, item];
      }
      return waitingList;
    }, { newData: []});

    const doesNotExistsSuggestions = this.context.get('doesNotExistsSuggestions') as boolean;
    const allSuggestionsHaveBeenSelected = this.context.get('allSuggestionsHaveBeenSelected') as boolean;
    const enableConfirmButton = allSuggestionsHaveBeenSelected || (waitingListReduced.newData.length > 0 && doesNotExistsSuggestions);
    this.wizard.setStepDone('suggestion', enableConfirmButton);
    if(!waitingListReduced.itemRemoved) {
      return;
    }

    this.context.set({ waitingListArrayToPersistence: waitingListReduced.newData });

    const deckDataTemp = this.deckChoosenItems.getData();
    const deckData = deckDataTemp as unknown as ScheduleItem[];

    const newDeckData: ScheduleItem[] = deckData.map<ScheduleItem>(item => {
      return item.nrSeqAgeintItem === waitingListReduced.itemRemoved?.nrSeqAgeintItem
        ? { ...item, isWaitingListItem: false }
        : item;
    });

    this.deckChoosenItems.setData(newDeckData as unknown as Record<string, Value>[]);
    this.deckChoosenItems.setDone(true);
  }

  private handleSubmitWaitingListModalByCard(event: SubmitEvent): void {
    const waitingListArrayToPersistenceFromContext = this.context.get('waitingListArrayToPersistence') as ScheduleWaitingListData[] || [];
    const waitingListItemToPersistenceFromModal = event.record as ScheduleWaitingListData;
    const waitingListArrayToPersistence: ScheduleWaitingListData[] = [
      ...waitingListArrayToPersistenceFromContext,
      waitingListItemToPersistenceFromModal
    ];
    this.context.set({ waitingListArrayToPersistence });
    const doesNotExistsSuggestions = this.context.get('doesNotExistsSuggestions') as boolean;
    const allSuggestionsHaveBeenSelected = this.context.get('allSuggestionsHaveBeenSelected') as boolean;
    const enableConfirmButton = allSuggestionsHaveBeenSelected || (waitingListArrayToPersistence.length > 0 && doesNotExistsSuggestions);
    this.wizard.setStepDone('suggestion', enableConfirmButton);
    this.handleAfterSubmitWaitingListModalByCard(waitingListItemToPersistenceFromModal);
  }

  private handleAfterSubmitWaitingListModalByCard(waitingListItemFromModal?: ScheduleWaitingListData): void {
    if(!waitingListItemFromModal) {
      return ;
    }

    const deckDataTemp = this.deckChoosenItems.getData();
    const deckData = deckDataTemp as unknown as ScheduleItem[];

    const newDeckData: ScheduleItem[] = deckData.map<ScheduleItem>(item => {
      return waitingListItemFromModal.nrSeqAgeintItem === item.nrSeqAgeintItem
        ? { ...item, isWaitingListItem: true }
        : { ...item, isWaitingListItem: !!item.isWaitingListItem };
    });
    this.deckChoosenItems.setData(newDeckData as unknown as Record<string, Value>[]);
    this.deckChoosenItems.setDone(true);
  }

  @OnClick({target: 'link-call-waiting-list'})
  handleButtonWaitingModalClick(): void {
    this.showWaitingListModal();
  }

  private setWaitingListDataToContext(): void {
    // nrSeqAgeint is initially 0, but its value is updated in context during steps.
    // Default values.
    const waitingListData: WaitingListData = {
      availablePeriod: [],
      consultScheduleList: [],
      dependencies: {
        calendarFinalDateInitialRange: this.state.finalDateInitialRange,
        currentPatientSelected: this.context.get<CurrentPatientSelected>('currentPatientSelected'),
        healthInsuranceCategoryId: '',
        healthInsuranceId: cast(this.cdConvenioParticular),
        healthInsurancePlanId: '',
      },
      examScheduleList: [],
      medicalSpecialities: cast([{}]),
      nrSeqAgeint: 0,
      nrSeqAgeintItem: '0',
      physician: cast({}),
      scheduleType: [],
    };

    this.context.set({ waitingListData });
  }

  /** Begin - handle steps result */

  @OnMount({target: 'form-datefield-result'})
  handleDatePickerAppointmentRangeResult(event: MountEvent): void {
    const formResult = event.target as FormAPI;
    const startAt = moment.utc(this.selectedDateRange.start);
    const endAt = moment.utc(this.selectedDateRange.end);
    formResult.setValue({selectedPeriodDatePicker: [startAt, endAt]});
  }

  /** End - handle steps result */
}
